import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Waits for a specific amount of time to allow tab content to fully render
 * @param {number} ms - Milliseconds to wait
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Captures a tab element as an image and adds it to the PDF
 * @param {HTMLElement} element - The DOM element to capture
 * @param {jsPDF} pdf - The jsPDF instance
 * @param {boolean} isFirstPage - Whether this is the first page (no page break needed)
 * @param {string} tabLabel - Label of the tab for debugging
 */
/**
 * Replaces textarea and input elements with styled divs so html2canvas
 * can render their text content with proper wrapping.
 * Returns a cleanup function that restores the original elements.
 */
const replaceFormElementsForCapture = (element) => {
  const replacements = [];

  // Replace textareas and inputs with divs containing their values
  const formElements = element.querySelectorAll('textarea, input[type="text"], input:not([type])');
  formElements.forEach(el => {
    const div = document.createElement('div');
    const computed = window.getComputedStyle(el);

    // Copy relevant styles
    div.style.cssText = `
      width: ${computed.width};
      min-height: ${computed.height};
      padding: ${computed.padding};
      border: ${computed.border};
      border-radius: ${computed.borderRadius};
      font-size: ${computed.fontSize};
      font-family: ${computed.fontFamily};
      line-height: ${computed.lineHeight};
      background-color: ${computed.backgroundColor};
      box-sizing: border-box;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      overflow: hidden;
    `;

    div.textContent = el.value || '';
    el.parentNode.insertBefore(div, el);
    el.style.display = 'none';
    replacements.push({ original: el, replacement: div });
  });

  return () => {
    replacements.forEach(({ original, replacement }) => {
      original.style.display = '';
      replacement.parentNode.removeChild(replacement);
    });
  };
};

const captureTabToPdf = async (element, pdf, isFirstPage, tabLabel) => {
  let cleanup = null;
  try {
    console.log(`Capturing tab: ${tabLabel}`);

    // Wait a bit for any final rendering/images to load
    await delay(500);

    // Replace form elements with divs for proper text wrapping in html2canvas
    cleanup = replaceFormElementsForCapture(element);

    // Capture the element as canvas
    const canvas = await html2canvas(element, {
      scale: 1, // Screen resolution (default)
      useCORS: true, // Allow cross-origin images
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    });

    // Use JPEG with quality compression for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.85);

    // PDF dimensions (A4 in portrait)
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Calculate image dimensions to fit page with margins
    const margin = 10;
    const availableWidth = pdfWidth - (2 * margin);
    const availableHeight = pdfHeight - (2 * margin);

    // Calculate scaling to fit within page
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);

    const scaledWidth = imgWidth * ratio;
    const scaledHeight = imgHeight * ratio;

    // Center the image on the page
    const xOffset = (pdfWidth - scaledWidth) / 2;
    const yOffset = margin;

    // Add new page if not first page
    if (!isFirstPage) {
      pdf.addPage();
    }

    // Add tab label at top of page
    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(tabLabel, margin, margin - 2);

    // Add image to PDF
    pdf.addImage(imgData, 'JPEG', xOffset, yOffset, scaledWidth, scaledHeight);

    console.log(`Successfully captured tab: ${tabLabel}`);
    return true;
  } catch (error) {
    console.error(`Error capturing tab ${tabLabel}:`, error);
    return false;
  } finally {
    if (cleanup) cleanup();
  }
};

/**
 * Generates a PDF from multiple tab elements
 * @param {Array<{element: HTMLElement, label: string}>} tabs - Array of tab elements and their labels
 * @param {string} fileName - Name for the PDF file
 * @param {Object} patientInfo - Patient information for PDF metadata
 * @param {string} password - Optional password for AES-128 encryption
 * @param {boolean} returnBase64 - If true, return base64 string instead of downloading
 */
export const generatePdfFromTabs = async (tabs, fileName = 'report.pdf', patientInfo = null, password = null, returnBase64 = false) => {
  try {
    console.log(`Starting PDF generation with ${tabs.length} tabs`);

    // Create new PDF document (A4 portrait) with optional encryption
    const pdfOptions = {
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    };

    // Add AES-128 encryption if password provided
    if (password && password.trim().length > 0) {
      pdfOptions.encryption = {
        userPassword: password,
        ownerPassword: password,
        userPermissions: ['print', 'copy'] // Allow printing and copying, prevent modification
      };
      console.log('PDF will be encrypted with AES-128');
    }

    const pdf = new jsPDF(pdfOptions);

    // Set PDF metadata
    if (patientInfo) {
      pdf.setProperties({
        title: `Report - ${patientInfo.name || 'Patient'}`,
        subject: `Case ID: ${patientInfo.caseId || ''}`,
        author: 'IPRS Pharos',
        keywords: 'report, assessment',
        creator: 'IPRS Pharos'
      });
    }

    let capturedCount = 0;

    // Capture each tab sequentially
    for (let i = 0; i < tabs.length; i++) {
      const { element, label } = tabs[i];

      if (!element) {
        console.warn(`Tab ${i} (${label}) has no element, skipping`);
        continue;
      }

      const success = await captureTabToPdf(
        element,
        pdf,
        capturedCount === 0, // isFirstPage
        label
      );

      if (success) {
        capturedCount++;
      }
    }

    if (capturedCount === 0) {
      throw new Error('No tabs were successfully captured');
    }

    console.log(`PDF generation complete. Captured ${capturedCount} of ${tabs.length} tabs`);

    const encryptionStatus = password && password.trim().length > 0 ? 'with AES-128 encryption' : 'without encryption';

    // Return base64 string or save the PDF
    if (returnBase64) {
      // Get the PDF as a base64 string (datauri format: "data:application/pdf;base64,...")
      const pdfDataUri = pdf.output('dataurlstring');
      // Extract just the base64 part after the comma
      const base64Pdf = pdfDataUri.split(',')[1];

      console.log(`PDF generated as base64 ${encryptionStatus}`);

      return {
        success: true,
        capturedCount,
        totalTabs: tabs.length,
        encrypted: !!(password && password.trim().length > 0),
        base64: base64Pdf
      };
    } else {
      // Save the PDF (encryption already applied if password was provided)
      pdf.save(fileName);
      console.log(`PDF saved ${encryptionStatus}`);

      return {
        success: true,
        capturedCount,
        totalTabs: tabs.length,
        encrypted: !!(password && password.trim().length > 0)
      };
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};
